import EventLogApi from '@zyro-inc/site-modules/api/EventLogApi';
import StoreApi from '@zyro-inc/site-modules/api/StoreApi';
import { loadStripeSdk } from '@zyro-inc/site-modules/components/blocks/ecommerce/utils';
import {
	EcommerceRegion,
	StripeAddressEvent,
	StripeConfirmEvent,
	StripeShippingRate,
	StripeShippingRateEvent,
	EcommerceUpdateCartDataProps,
	EcommerceCart,
	EcommerceProductType,
	ECOMMERCE_SHIPPING_PROVIDER_TYPE,
} from '@zyro-inc/site-modules/types';
import { isValidPrice } from '@zyro-inc/site-modules/utils/ecommerce/priceFormatter';
import {
	computed,
	ComputedRef,
	ref,
} from 'vue';

type StripeElement = {
	create: (type: string, options: any) => any;
	update: (options: any) => void;
	mount: (element: HTMLElement) => void;
	on: (event: string, handler: (event: any) => void) => void;
};

type Stripe = {
	elements: (payload: {
		mode?: string,
		currency?: string,
		amount?: number,
		onBehalfOf?: string | null,
	}) => StripeElement,
	confirmPayment: (options: {
		clientSecret: string,
		elements: StripeElement,
		confirmParams: { return_url: string, payment_method: string }
	}) => Promise<{ error?: Error }>
};

export const getShippingAddressDetails = (event: StripeAddressEvent | StripeConfirmEvent, isProductPhysical: boolean) => {
	const confirmEvent = event as StripeConfirmEvent;

	if (isProductPhysical && 'address' in event) {
		return {
			shippingAddress: event.address,
			shippingCountryCode: event.address.country?.toLowerCase(),
			shippingStateCode: event.address.state
				? `${event.address.country.toLowerCase()}-${event.address.state.toLowerCase()}`
				: undefined,
			shippingName: event.name,
		};
	}

	if (!isProductPhysical || !confirmEvent.shippingAddress) {
		return {};
	}

	const shippingAddress = confirmEvent.shippingAddress.address;
	const shippingCountryCode = shippingAddress.country?.toLowerCase();
	const shippingStateCode = shippingAddress.state ? `${shippingCountryCode}-${shippingAddress.state?.toLowerCase()}` : undefined;
	const shippingName = confirmEvent.shippingAddress.name;
	const shippingPhone = confirmEvent.billingDetails?.phone;

	return {
		shippingAddress,
		shippingCountryCode,
		shippingStateCode,
		shippingName,
		shippingPhone,
	};
};

export const getBillingAddressDetails = (event: StripeAddressEvent | StripeConfirmEvent, isProductPhysical: boolean) => {
	// shipping address is not returned when product is non-physical so we will receive only billingDetails on conforim event
	if (isProductPhysical && !('billingDetails' in event)) {
		return {
			billingAddress: event.address,
			billingCountryCode: event.address.country?.toLowerCase(),
			billingStateCode: event.address.state
				? `${event.address.country?.toLowerCase()}-${event.address.state.toLowerCase()}`
				: undefined,
			billingName: event.name,
			billingEmail: event.email,
			billingPhone: event.phone,
		};
	}

	const billingAddress = (event as StripeConfirmEvent).billingDetails.address;
	const billingCountryCode = billingAddress.country.toLowerCase();
	const billingStateCode = billingAddress.state ? `${billingCountryCode}-${billingAddress.state.toLowerCase()}` : undefined;
	const billingEmail = (event as StripeConfirmEvent).billingDetails.email;
	const billingPhone = (event as StripeConfirmEvent).billingDetails.phone;

	return {
		billingAddress,
		billingCountryCode,
		billingStateCode,
		billingName: (event as StripeConfirmEvent).billingDetails.name,
		billingEmail,
		billingPhone,
	};
};

export const isCartOnlyPrintOnDemand = async (cart: EcommerceCart): Promise<boolean> => {
	const storeId = cart.store_id;

	if (!cart?.items?.length || !storeId) {
		return false;
	}

	try {
		const productIds = [...new Set(cart.items.map((item) => item.variant.product_id))];

		const products = await StoreApi.getStoreProductsByIds(storeId, productIds);

		return products.every((product) => product.type.value === EcommerceProductType.PRINT_ON_DEMAND);
	} catch (error) {
		console.error('Error checking print-on-demand products:', error);

		return false;
	}
};

export const useExpressCheckout = ({
	isProductPhysical,
	updateCartData,
	currencyCode,
	productPrice,
	selectedQuantity,
}: {
	productPrice: ComputedRef<number>,
	isProductPhysical: ComputedRef<boolean>,
	updateCartData?: (payload: EcommerceUpdateCartDataProps) => Promise<EcommerceCart | undefined>,
	currencyCode: ComputedRef<string>,
	selectedQuantity: ComputedRef<number>,
}) => {
	const isStripeMounted = ref(false);
	const stripe = ref<Stripe>(null as unknown as Stripe);
	const elements = ref<StripeElement>(null as unknown as StripeElement);
	const expressCheckoutElement = ref<any>(null);
	const shippingRates = ref<StripeShippingRate[]>([]);
	const selectedShippingRate = ref<StripeShippingRate | null>(null);
	const selectedRegion = ref<EcommerceRegion>();
	const isUpdating = ref(false);
	const isShippingRateUpdating = ref(false);
	const cartData = ref<EcommerceCart>({} as EcommerceCart);

	const totalPriceAmount = computed(() => productPrice.value * selectedQuantity.value + (selectedShippingRate.value?.amount || 0));

	const setSelectedShippingRate = (rate: StripeShippingRate) => {
		selectedShippingRate.value = rate;
	};

	const getShippingRates = async (cart?: EcommerceCart) => {
		if (!isProductPhysical.value || !cart?.id) {
			return;
		}

		try {
			// If cart contains only print-on-demand products, filter for manual shipping only
			const isOnlyPrintOnDemand = cart ? await isCartOnlyPrintOnDemand(cart) : false;
			const providerId = isOnlyPrintOnDemand ? ECOMMERCE_SHIPPING_PROVIDER_TYPE.MANUAL : undefined;

			const shippingOptions = await StoreApi.getCartShippingOptions(cart.id, providerId);

			shippingRates.value = shippingOptions;
			[selectedShippingRate.value] = shippingRates.value;
		} catch (error) {
			console.error(error);
		}
	};

	const updateStripeElement = ({ price }: { price: number}) => {
		if (!isValidPrice(price)) {
			return;
		}

		elements.value?.update({
			amount: price,
		});
	};

	const setupStripeElements = async (
		stripePublicKey: string,
		expressCheckoutElementContainerRef: HTMLElement | null,
		location?: string,
	) => {
		try {
			await loadStripeSdk();
			// @ts-ignore
			// eslint-disable-next-line no-undef
			stripe.value = Stripe(stripePublicKey);

			const initialAmount = totalPriceAmount.value;

			if (!isValidPrice(initialAmount)) {
				return;
			}

			elements.value = stripe.value.elements({
				mode: 'payment',
				currency: currencyCode.value,
				amount: initialAmount,
			});

			expressCheckoutElement.value = elements.value.create('expressCheckout', {
				layout: {
					maxColumns: 1,
					maxRows: 3,
				},
			});

			expressCheckoutElement.value.on('click', (event: any) => {
				EventLogApi.logEvent({
					eventName: 'builder_customers.ecomm_express_checkout.enter',
					eventProperties: {
						...(location && {
							location,
						}),
						payment_method: event.expressPaymentType || 'unknown',
					},
				});
			});

			if (!expressCheckoutElementContainerRef) {
				return;
			}

			expressCheckoutElement.value.mount(expressCheckoutElementContainerRef);

			isStripeMounted.value = true;
		} catch (error) {
			console.error('Error setting up Stripe:', error);
		}
	};

	const updateShippingOptions = async ({
		cart,
		reset,
	}: {cart: EcommerceCart, reset?: boolean}) => {
		if (!isProductPhysical.value) {
			return cart;
		}

		let shippingOptions;

		if (!cart?.shipping_options) {
			shippingOptions = await getShippingRates(cart);
		} else {
			shippingOptions = cart?.shipping_options;
		}

		if (shippingOptions && isProductPhysical.value) {
			shippingRates.value = shippingOptions
				.map((option) => ({
					id: option.id,
					displayName: option.name,
					provider: option.provider_id,
					amount: option.amount ?? 0,
				}));

			const isShippingRateSelected = selectedShippingRate.value
				&& shippingRates.value.some((rate) => rate.id === selectedShippingRate.value?.id);

			if (!isShippingRateSelected || reset) {
				[selectedShippingRate.value] = shippingRates.value;
			}

			if (selectedShippingRate.value && cart.shipping_methods[0]?.id !== selectedShippingRate.value.id) {
				const updatedCart = await StoreApi.setShippingMethod({
					cartId: cart.id,
					optionId: selectedShippingRate.value.id,
				});

				return updatedCart;
			}
		}

		return cart;
	};

	const combineAddress = (...addressLines: (string | undefined | null)[]) => addressLines.filter(Boolean).join(' ').trim() || undefined;

	const handleAddressChangeEvent = async (
		event: StripeAddressEvent | StripeConfirmEvent,
		cartId: string,
		regions?: EcommerceRegion[],
		// eslint-disable-next-line consistent-return
	): Promise<{resolve?: boolean} | undefined> => {
		if (isUpdating.value) {
			return {
				resolve: false,
			};
		}

		const {
			shippingAddress,
			shippingCountryCode,
			shippingStateCode,
			shippingName,
			shippingPhone,
		} = getShippingAddressDetails(event, isProductPhysical.value);

		if (isProductPhysical.value) {
			selectedRegion.value = regions?.find(
				(region) => region.countries.find((country) => {
					const foundCountry = country.countryCode === shippingCountryCode;

					if (country.provinces?.length) {
						const foundState = country.provinces.find((provinceCode) => provinceCode === shippingStateCode);

						return !!foundCountry && !!foundState;
					}

					return !!foundCountry;
				}),
			);

			if (!selectedRegion.value) {
				if ('reject' in event) {
					event.reject();
				} else {
					event.paymentFailed({
						reason: 'invalid_shipping_address',
					});
				}

				return {
					resolve: true,
				};
			}
		}

		const isSelectedCountryWithState = !!selectedRegion.value?.countries
			.find((country) => country.countryCode === shippingCountryCode)?.provinces?.length;

		const {
			billingAddress,
			billingCountryCode,
			billingStateCode,
			billingName,
			billingEmail,
			billingPhone,
		} = getBillingAddressDetails(event, isProductPhysical.value);

		const shippingAddressToUpdate = {
			...shippingAddress,
			country: shippingCountryCode,
			state: isSelectedCountryWithState ? shippingStateCode : undefined,
			name: shippingName,
			line1: shippingAddress ? combineAddress(shippingAddress.line1, shippingAddress.line2) : undefined,
			phone: shippingPhone,
		};
		const billingAddressToUpdate = {
			...billingAddress,
			country: billingCountryCode,
			state: isSelectedCountryWithState ? billingStateCode : undefined,
			name: billingName,
			line1: billingAddress ? combineAddress(billingAddress.line1, billingAddress.line2) : undefined,
			phone: billingPhone,
		};

		try {
			isUpdating.value = true;
			let updatedCart;

			updatedCart = await updateCartData?.({
				cartId,
				shippingAddress: shippingAddressToUpdate,
				billingAddress: billingAddressToUpdate,
				regionId: selectedRegion.value?.id,
				...(billingEmail ? {
					email: billingEmail,
				} : {}),
			});

			if (updatedCart) {
				updatedCart = await updateShippingOptions({
					cart: updatedCart,
				});

				cartData.value = updatedCart;

				updateStripeElement({
					price: updatedCart.total,
				});
			}

			return {
				resolve: true,
			};
		} catch {
			if ('reject' in event) {
				event.reject();
			} else {
				event.paymentFailed({
					reason: 'invalid_shipping_address',
				});
			}
		} finally {
			isUpdating.value = false;
		}
	};

	const updateExpressCartProducts = async ({
		cartId,
		itemsIdsToAdd,
	}: {cartId: string, itemsIdsToAdd: {variantId: string, quantity: number}[]}) => {
		if (cartId === 'demo') {
			return null;
		}

		let newCartData = await StoreApi.resetCart({
			cartId,
		});

		try {
			if (itemsIdsToAdd.length) {
				newCartData = await StoreApi.addCartItems({
					cartId,
					items: itemsIdsToAdd,
				});

				newCartData = await updateShippingOptions({
					cart: newCartData,
					reset: true,
				});
			}

			if (newCartData) {
				updateStripeElement({
					price: newCartData?.total,
				});
			}

			return newCartData;
		} catch {
			return null;
		}
	};

	const handleShippingRateChange = async (event: StripeShippingRateEvent, cartId: string) => {
		if (isShippingRateUpdating.value) {
			return {
				preventResolve: true,
			};
		}

		isShippingRateUpdating.value = true;
		const updatedCart = await StoreApi.setShippingMethod({
			cartId,
			optionId: event.shippingRate.id,
		});

		selectedShippingRate.value = event.shippingRate;

		if (updatedCart) {
			updateStripeElement({
				price: updatedCart.total,
			});
		}

		isShippingRateUpdating.value = false;

		return updatedCart;
	};

	return {
		expressCheckoutElement,
		elements,
		stripe,
		totalPriceAmount,
		shippingRates,
		selectedRegion,
		getShippingRates,
		handleAddressChangeEvent,
		selectedShippingRate,
		setSelectedShippingRate,
		handleShippingRateChange,
		setupStripeElements,
		updateStripeElement,
		isStripeMounted,
		updateExpressCartProducts,
	};
};
